// React/Next imports
import { useEffect, useState } from 'react';

import clsx from 'clsx';
// Third-party libraries
import { useAtomValue } from 'jotai';

// Utils/Helpers
import { getMenuLocalStorage } from '@/utils/localStorage/menuLocalStorage';
import { useToggleLayoutPlatform } from '@/utils/zustand/providers/toggle-layout-platform-provider';
// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';
import useGetGlobalInfo from '@/utils/layout/globalInfoDisclaimer/useGetGlobalInfo';

// Components
import { HeaderPlatform, LayoutPlatform } from '@/components/layouts/LayoutPlatform';
import { AccessControl } from '@/components/middleware/AccessControl';
import { PaddleInitialize } from '@/components/tools/Paddle';
import { MenuDesktop } from '@/components/layouts/LayoutPlatform/components/Menu/MenuDesktop';
import { PastDueBanner } from '@/components/layouts/LayoutPlatform/components/Header/components/GlobalInfoDisclaimer/Popover/Banners/PastDueBanner';
// Atoms/Store
import { menuOverlayAtom } from '@/components/layouts/LayoutPlatform/components/Menu/menuOverlayAtom';
import { PromotedPostsUpsellBanner } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/components/PromotedPostsUpsellBanner';
import { ReminderSubscriptionEndBanner } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/components/ReminderSubscriptionEndBanner';

export type WrapperPlatformProps = {
    noPadding?: boolean;
    disableMainScroll?: boolean;
    className?: string;
    children: React.ReactNode;
};

export function WrapperPlatform({
    noPadding = false,
    disableMainScroll = false,
    className,
    children
}: WrapperPlatformProps) {
    const { useAddDarkMode } = useDarkMode();

    const updatePlatformLayout = useToggleLayoutPlatform((state) => state.updatePlatformLayout);

    useEffect(() => {
        updatePlatformLayout?.(true);
        return () => updatePlatformLayout(false);
    }, []);

    // Launch theme
    useAddDarkMode();

    return (
        <LayoutPlatform>
            <AccessControl>
                <WrapperPlatformContent
                    className={className}
                    noPadding={noPadding}
                    disableMainScroll={disableMainScroll}>
                    {children}
                </WrapperPlatformContent>
            </AccessControl>
            <PaddleInitialize />
        </LayoutPlatform>
    );
}

function WrapperPlatformContent({
    noPadding = false,
    disableMainScroll = false,
    className,
    children
}: WrapperPlatformProps) {
    const [stickyMenu, setStickyMenu] = useState(getMenuLocalStorage());
    const menuOverlay = useAtomValue(menuOverlayAtom);
    const { banners } = useGetGlobalInfo();

    return (
        <>
            <MenuDesktop stickyMenu={stickyMenu} setStickyMenu={setStickyMenu} />
            <div
                className={clsx('flex max-w-full flex-1 flex-col px-3 sm:pl-0', {
                    'sm:w-[calc(100%-72px)]': !stickyMenu,
                    'sm:w-[calc(100%-208px)]': stickyMenu
                })}>
                <HeaderPlatform />
                <div
                    id="wrapper-logged"
                    className={clsx(
                        'main-content-scroll relative z-0 flex flex-1 flex-col rounded-t-2xl bg-white/40 @container/wrapper-platform-content dark:bg-icoDarkMode-deepBlack/40',
                        { scrollbar: !disableMainScroll, 'overflow-hidden': disableMainScroll }
                    )}>
                    <ReminderSubscriptionEndBanner />
                    <PromotedPostsUpsellBanner />
                    <PastDueBanner bannerInfo={banners.pastDue} />
                    <div
                        className={clsx(className, 'flex flex-1 flex-col', {
                            'px-5 py-5 sm:px-8 sm:py-8': !noPadding,
                            scrollbar: disableMainScroll
                        })}>
                        {children}
                    </div>
                </div>
            </div>
            {menuOverlay}
        </>
    );
}
